import React from 'react';
import { PieChart as RechartsPieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { ChartProps } from '../../types/visualization';
import CustomTooltip from '../common/CustomTooltip';

const PieChart: React.FC<ChartProps> = ({ data, config, colors = ['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6', '#06b6d4', '#84cc16', '#f97316'] }) => {
  if (!data || data.length === 0) {
    return <div>No data available</div>;
  }

  // Get configuration
  const labelColumn = config?.xAxis?.column || Object.keys(data[0])[0];
  const valueColumn = config?.yAxis?.primary?.columns?.[0] || Object.keys(data[0]).find(key => key !== labelColumn) || Object.keys(data[0])[1];

  // Format data for Recharts
  const formattedData = data.map((item, index) => ({
    name: String(item[labelColumn]),
    value: Number(item[valueColumn]) || 0,
    color: colors[index % colors.length]
  }));

  // Calculate total for percentage display
  const total = formattedData.reduce((sum, item) => sum + item.value, 0);


  const CustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }: any) => {
    // Only show label if slice is large enough (>5%)
    if (percent < 0.05) return null;
    
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text 
        x={x} 
        y={y} 
        fill="white" 
        textAnchor={x > cx ? 'start' : 'end'} 
        dominantBaseline="central"
        fontSize={12}
        fontWeight={600}
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <div style={{ width: '100%', height: '400px' }}>
      <ResponsiveContainer width="100%" height="100%">
        <RechartsPieChart margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
          <Pie
            data={formattedData}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={<CustomLabel />}
            outerRadius={120}
            fill="#8884d8"
            dataKey="value"
          >
            {formattedData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip 
            content={({ active, payload }: any) => {
              if (active && payload && payload.length) {
                const data = payload[0].payload;
                const percentage = total > 0 ? ((data.value / total) * 100).toFixed(1) : 0;
                
                // Transform payload to match CustomTooltip format
                const transformedPayload = [{
                  name: 'Value',
                  value: data.value,
                  color: data.color,
                  payload: { percentage: parseFloat(percentage.toString()) }
                }];
                
                return (
                  <CustomTooltip 
                    active={active}
                    payload={transformedPayload}
                    label={data.name}
                    config={config}
                    xAxisLabel={config?.xAxis?.label || 'Category'}
                    showPercentage={true}
                    currencyFormat={config?.yAxis?.primary?.format === 'currency'}
                  />
                );
              }
              return null;
            }}
          />
          <Legend 
            verticalAlign="bottom" 
            height={36}
            formatter={(value, entry: any) => (
              <span style={{ color: entry.color, fontSize: '12px' }}>
                {value}
              </span>
            )}
          />
        </RechartsPieChart>
      </ResponsiveContainer>
    </div>
  );
};

export default PieChart; 